import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { I18n } from 'aws-amplify';
import { fetchUserAccounts, updateAccountUser } from '../../components/util/callApi';
import { ACCOUNT_USER_CONFIRMATION_STATUS } from '../../configures/val';
import Button from '../../components/common/Button';
import styles from './styles.module.css';

/** 確認・キャンセル後のリダイレクトやマイページに戻る際のリンク先 URL */
const homePath = '/';

/**
 * ユーザ追加確認画面
 * @param {Object} props
 * @param {Function} props.updateHeader - ヘッダの更新メソッド
 * @param {string|undefined} props.userSub - ユーザの sub
 * @returns {JSX.Element}
 */
const AccountAffiliationConfirmation = ({ history, location, updateHeader, userSub }) => {
  const updateHeaderRef = useRef(updateHeader);
  const [account, setAccount] = useState(null);
  const [isInvalidUrl, setIsInvalidUrl] = useState(false);

  /**
   * アカウント ID はクエリパラメータから取得
   */
  const accountId = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return Number(query.get('accountId'));
  }, [location.search]);

  /**
   * 追加を拒否する
   */
  const reject = useCallback(async () => {
    await updateAccountUser(accountId, userSub, {
      confirmationStatus: ACCOUNT_USER_CONFIRMATION_STATUS.REJECTED,
    });
    history.push(homePath);
  }, [accountId, userSub, history]);

  /**
   * 追加を確認(承諾)する
   */
  const confirm = useCallback(async () => {
    await updateAccountUser(accountId, userSub, {
      confirmationStatus: ACCOUNT_USER_CONFIRMATION_STATUS.CONFIRMED,
    });
    history.push(homePath);
  }, [accountId, userSub, history]);

  // ヘッダを更新する
  useEffect(() => {
    if (updateHeaderRef.current) updateHeaderRef.current();
  }, []);

  // アカウント情報を取得し状態変数にセットする
  useEffect(() => {
    if (!(accountId && userSub)) return;
    (async () => {
      const { ok, data } = await fetchUserAccounts(userSub);
      if (!ok) return;
      const targetAccount = data.accounts.find(({ id }) => id === accountId);
      if (targetAccount) {
        setAccount(targetAccount);
      } else {
        return setIsInvalidUrl(true);
      }
      // 追加できるのはアカウントユーザとしての確認状況が「未確認」の場合のみ
      if (targetAccount?.confirmationStatus !== ACCOUNT_USER_CONFIRMATION_STATUS.UNCONFIRMED)
        setIsInvalidUrl(true);
    })();
  }, [accountId, userSub]);

  if (!(account || isInvalidUrl)) return <>{I18n.get('Now loading...')}</>;

  return (
    <div className={styles.container}>
      {isInvalidUrl ? (
        <>
          <h2 className={`${styles.title} ${styles.center}`}>
            {I18n.get('This URL is not valid.')}
          </h2>
          <div className={styles.mypageBtnArea}>
            <Button
              type='link'
              text={I18n.get('Go to Laplace ID My page')}
              linkType='internal'
              linkTo={homePath}
            />
          </div>
        </>
      ) : (
        <>
          <h2 className={styles.title}>
            {I18n.get('Tentatively added to the following account.')}
          </h2>
          <div className={styles.accountInfo}>
            <p className={styles.label}>{I18n.get('Account')}</p>
            <p className={styles.name}>{account.displayName}</p>
            <small>
              {I18n.get(
                '*If you are not familiar with the target account, please click the Cancel button. Cancel the tentatively addition to the account.'
              )}
            </small>
          </div>
          <div className={styles.buttonArea}>
            <Button color='white' text={I18n.get('Cancel')} onClick={reject} />
            <Button color='rich' text={I18n.get('Confirm addition')} onClick={confirm} />
          </div>
        </>
      )}
    </div>
  );
};

export default withRouter(AccountAffiliationConfirmation);
